import { FC, useMemo } from 'react';
import {
  ScheduledCall,
  useGetScheduledCallsQuery,
  useGetUserProjectsQuery,
  useGetCurrentUserQuery,
  UserProject,
  UserProjectStatus,
} from '@teachur/http-client';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { timestampToDate } from '@new/common/lib/dateTime';
import { DashboardList } from '../DashboardList/DashboardList';
import { mapCallToOverdueItem, mapOverdueProjectToItem } from '../../lib/helpers';
import {
  CALLS_PAGE_SIZE,
  PROJECTS_PAGE_SIZE,
  DASHBOARD_LIST_LIMIT,
  MILLISECONDS_PER_DAY,
} from '../../constants';

const DUE_DAYS = 5;
const GRACE_DAYS = 2;

interface ActionRequiredProps {
  onSelectProject: (project: UserProject) => void;
  onSelectCall?: (call: ScheduledCall) => void;
}

export const ActionRequired: FC<ActionRequiredProps> = ({ onSelectProject, onSelectCall }) => {
  const { openVideoCall } = useVideoCallContext();
  const { data: currentUser } = useGetCurrentUserQuery();
  const currentUserId = currentUser?.id;

  const { data: acceptedData } = useGetScheduledCallsQuery(
    currentUserId
      ? { status: 'accepted', instructorId: currentUserId, page: 1, pageSize: CALLS_PAGE_SIZE }
      : { status: 'accepted', page: 1, pageSize: CALLS_PAGE_SIZE }
  );
  const { data: projectsData } = useGetUserProjectsQuery(
    currentUserId
      ? {
          status: UserProjectStatus.InReview,
          instructorId: currentUserId,
          page: 1,
          pageSize: PROJECTS_PAGE_SIZE,
        }
      : undefined
  );

  const acceptedCalls = acceptedData?.data ?? [];
  const inReviewProjects = projectsData?.data ?? [];

  const { missedCalls, overdueProjects } = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTodayMs = startOfToday.getTime();

    const overdue = inReviewProjects.filter((p) => {
      if (!p.claimedAt) return false;
      const daysElapsed = Math.floor(
        (Date.now() - new Date(p.claimedAt).getTime()) / MILLISECONDS_PER_DAY
      );
      return daysElapsed > DUE_DAYS + GRACE_DAYS;
    });

    const missed = acceptedCalls.filter((call) => {
      const isPastCall = timestampToDate(call.startTime).getTime() < startOfTodayMs;
      const wasCallHeld = call.videoCall?.endedAt;
      return isPastCall && !wasCallHeld;
    });

    return {
      missedCalls: missed,
      overdueProjects: overdue,
    };
  }, [acceptedCalls, inReviewProjects]);

  const items = useMemo(
    () =>
      [
        ...overdueProjects.map((project) => mapOverdueProjectToItem(project, onSelectProject)),
        ...missedCalls.map((call) => mapCallToOverdueItem(call, openVideoCall, onSelectCall)),
      ].slice(0, DASHBOARD_LIST_LIMIT),
    [missedCalls, overdueProjects, openVideoCall, onSelectProject, onSelectCall]
  );

  return (
    <DashboardList title="Action Required" items={items} variant="overdue" className="flex-1" />
  );
};
